import React, { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import PreviewCanvas from './PreviewCanvas';
import { ApplyVideoWorkspaceBlocks } from '../../../Client/ManagementServiceClient';
import './effectBlocks.css';

const FONTS = ['Futura Bold', 'Segoe UI Semibold', 'Impact', 'Georgia Italic'];
const COLORS = ['#ffffff', '#ffe14d', '#d9363e', '#111111'];
const POSITIONS = [
    { label: 'Top', y: 0.18 },
    { label: 'Middle', y: 0.5 },
    { label: 'Lower', y: 0.78 },
];

const DEFAULT_PARAMS = {
    text: 'Your text here',
    x: 0.5,
    y: 0.18,
    anchor: 'center',
    fontFrac: 0.06,
    color: '#ffffff',
    shadow: true,
    box: false,
    fontFamily: 'Futura Bold',
};

/**
 * TextBlockEditor — phase-1 effect block: place, size, color and time a text
 * overlay on a live canvas, then compile it into the plan via ApplyBlocks.
 *
 * Props:
 *   videoWorkspaceId - target workspace
 *   frameUrl         - a preview URL of the source clip (video)
 *   sourceFileName   - the asset FileName the text is drawn on
 *   mediaInfo        - { width, height, duration } of that asset
 *   aiSuggestion     - optional { params, rationale } to seed the block
 *   onApplied        - () => void, called after a successful queue
 *   onClose          - () => void
 */
function TextBlockEditor({ videoWorkspaceId, frameUrl, sourceFileName, mediaInfo, aiSuggestion, onApplied, onClose }) {
    const [params, setParams] = useState(() => ({ ...DEFAULT_PARAMS, ...(aiSuggestion?.params || {}) }));
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');
    // Real dimensions are measured from the loaded video; the mediaInfo prop is
    // only a seed until then, so placement is always resolution-correct.
    const [measured, setMeasured] = useState(() => (
        Number(mediaInfo?.width) > 0 && Number(mediaInfo?.height) > 0 ? mediaInfo : null
    ));

    const aspectRatio = useMemo(() => {
        const w = Number(measured?.width) || 9;
        const h = Number(measured?.height) || 16;
        return w / h;
    }, [measured]);

    const block = useMemo(() => ({ id: 'text-1', type: 'text', source: sourceFileName, params }), [params, sourceFileName]);

    const update = (patch) => setParams((prev) => ({ ...prev, ...patch }));

    const canApply = Boolean(
        videoWorkspaceId && sourceFileName &&
        Number(measured?.width) > 0 && Number(measured?.height) > 0 &&
        String(params.text || '').trim()
    );

    const handleApply = async () => {
        if (!canApply) {
            setError('Add some text and make sure the clip has loaded.');
            return;
        }
        setApplying(true);
        setError('');
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const outBlock = {
                id: 'text-1',
                type: 'text',
                source: sourceFileName,
                startTime: 0,
                endTime: null, // phase 1: whole clip
                params,
            };
            const media = {
                [sourceFileName]: {
                    width: Math.round(Number(measured.width)),
                    height: Math.round(Number(measured.height)),
                    duration: Number(measured.duration) || 0,
                },
            };
            const res = await ApplyVideoWorkspaceBlocks(idToken, videoWorkspaceId, [outBlock], media);
            if (res.status === 200) {
                onApplied?.(res.myJson);
            } else {
                setError(res.myJson?.detail || 'Failed to apply the text edit.');
            }
        } catch (err) {
            setError('Error applying edit: ' + err.message);
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="eb-editor">
            {aiSuggestion?.rationale && (
                <div className="eb-ai-banner">
                    <span className="eb-ai-badge">AI</span>
                    <span>{aiSuggestion.rationale}</span>
                </div>
            )}
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[block]}
                    selectedId="text-1"
                    onChange={(_id, next) => setParams(next)}
                    onSelect={() => {}}
                    onMeta={setMeasured}
                />
                <div className="eb-controls">
                    <h5>Text</h5>

                    <label className="eb-ctl">
                        <span>Content</span>
                        <input
                            className="eb-input"
                            value={params.text}
                            maxLength={200}
                            onChange={(e) => update({ text: e.target.value })}
                        />
                    </label>

                    <label className="eb-ctl">
                        <span>Font</span>
                        <select className="eb-input" value={params.fontFamily} onChange={(e) => update({ fontFamily: e.target.value })}>
                            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </label>

                    <label className="eb-ctl">
                        <span>Size <em>{Math.round((params.fontFrac || 0) * 100)}%</em></span>
                        <input
                            type="range" min="2" max="16" step="0.5"
                            value={(params.fontFrac || 0.06) * 100}
                            onChange={(e) => update({ fontFrac: Number(e.target.value) / 100 })}
                        />
                    </label>

                    <div className="eb-ctl">
                        <span>Position</span>
                        <div className="eb-seg">
                            {POSITIONS.map((pos) => (
                                <button
                                    key={pos.label}
                                    type="button"
                                    aria-pressed={Math.abs((params.y || 0) - pos.y) < 0.02}
                                    onClick={() => update({ y: pos.y })}
                                >
                                    {pos.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="eb-ctl">
                        <span>Alignment</span>
                        <div className="eb-seg">
                            {['left', 'center', 'right'].map((a) => (
                                <button key={a} type="button" aria-pressed={params.anchor === a} onClick={() => update({ anchor: a })}>
                                    {a[0].toUpperCase() + a.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="eb-ctl">
                        <span>Color</span>
                        <div className="eb-swatches">
                            {COLORS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    className="eb-sw"
                                    style={{ background: c }}
                                    aria-pressed={params.color === c}
                                    aria-label={`Color ${c}`}
                                    onClick={() => update({ color: c })}
                                />
                            ))}
                        </div>
                    </div>

                    <label className="eb-check">
                        <input type="checkbox" checked={!!params.shadow} onChange={(e) => update({ shadow: e.target.checked })} />
                        <span>Drop shadow</span>
                    </label>
                    <label className="eb-check">
                        <input type="checkbox" checked={!!params.box} onChange={(e) => update({ box: e.target.checked })} />
                        <span>Background box</span>
                    </label>

                    {error && <p className="eb-error">{error}</p>}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Cancel</button>}
                        <button type="button" className="eb-btn primary" disabled={!canApply || applying} onClick={handleApply}>
                            {applying ? 'Applying…' : 'Add to timeline'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TextBlockEditor;
